import React, { FC, ReactNode, useCallback, useState } from 'react';
import { Cancel } from '@mui/icons-material';
import { CircularProgress, Dialog, IconButton, InputLabel, SelectChangeEvent } from '@mui/material';
import { toast } from 'react-toastify';
import isEmail from 'validator/lib/isEmail';
import API from 'api/API';
import Button from 'components/Button';
import DropDown from 'components/DropDown/DropDown';
import InputField from 'components/InputField';
import { useLanguage } from 'context/AppContext';
import { MenuElements } from 'pages/UserManagement/UserManagementPage';
import { useUser } from 'store/user/useUser';
import { isInMaxKelsenOrg } from 'utils/helpers/permissions.helpers';
import * as Styles from './CreateUserModal.styles';

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  menuElements: MenuElements;
}

interface DropDownWithLabelProps {
  menuElements: ReactNode[];
  label: string;
  value?: string;
  onChange: (event: SelectChangeEvent<unknown>) => void;
}

const CreateUserModal: FC<CreateUserModalProps> = ({ open, onClose, onSubmit: onSubmitExternal, menuElements }) => {
  const user = useUser();
  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [roleId, setRoleId] = useState<string | undefined>(undefined);
  const [orgId, setOrgId] = useState<string | undefined>(user.isAuthenticated ? user.org_id : undefined);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const isCreateUserButtonDisabled = !(isEmail(userEmail) && !!roleId && !!orgId && !!locationId);
  const [
    {
      UserManagement: {
        CreateModal: {
          successMessage,
          errorMessage,
          title,
          body,
          email,
          emailPlaceholder,
          role,
          location,
          organisation,
          submit,
        },
      },
    },
  ] = useLanguage();

  const onSubmit = useCallback(() => {
    setLoading(true);
    API.userManagement
      .createUser(userEmail, roleId, orgId, locationId)
      .then(() => toast.success(successMessage))
      .catch(error => toast.error(errorMessage + error))
      .finally(() => {
        setLoading(false);
        onSubmitExternal();
      });
  }, [userEmail, locationId, onSubmitExternal, orgId, roleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const DropDownWithLabel: FC<DropDownWithLabelProps> = ({ menuElements, label, value, onChange }) => {
    const labelId = `create-user-modal-` + label;
    return (
      <Styles.InputContainer fullWidth>
        <InputLabel id={labelId}>{label}</InputLabel>
        <DropDown labelId={labelId} value={value} label={label} onChange={onChange}>
          {menuElements}
        </DropDown>
      </Styles.InputContainer>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <Styles.Container>
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <IconButton sx={{ position: 'absolute', top: '1rem', right: '1rem' }} onClick={onClose}>
              <Cancel />
            </IconButton>
            <h2>{title}</h2>
            <Styles.Message>{body}</Styles.Message>
            <Styles.InputContainer fullWidth>
              <InputField
                label={email}
                variant="outlined"
                onChange={event => setUserEmail(event.target.value)}
                placeholder={emailPlaceholder}
                type="email"
                {...{ email }}
              />
            </Styles.InputContainer>
            <DropDownWithLabel
              menuElements={menuElements.roles}
              label={role}
              onChange={event => setRoleId(event.target.value as string)}
              value={roleId}
            />
            <DropDownWithLabel
              menuElements={menuElements.locations}
              label={location}
              onChange={event => setLocationId(event.target.value as string)}
              value={locationId}
            />
            {isInMaxKelsenOrg(user) && (
              <DropDownWithLabel
                menuElements={menuElements.organisations}
                label={organisation}
                onChange={event => setOrgId(event.target.value as string)}
                value={orgId}
              />
            )}
            <Button onClick={onSubmit} fullWidth disabled={isCreateUserButtonDisabled}>
              {submit}
            </Button>
          </>
        )}
      </Styles.Container>
    </Dialog>
  );
};

export default CreateUserModal;
